// OKR (Objectives & Key Results) Types

export type OKRTimePeriod = 'monthly' | 'quarterly' | 'annual' | 'custom';

export type OKRStatus = 'draft' | 'active' | 'at_risk' | 'behind' | 'completed' | 'cancelled';

export type KeyResultMetricType = 'percentage' | 'number' | 'currency' | 'boolean' | 'custom';

export type KeyResultStatus = 'not_started' | 'on_track' | 'at_risk' | 'behind' | 'completed' | 'cancelled';

export type KeyResultConfidence = 'high' | 'medium' | 'low';

export interface OKR {
  id: string;
  workspaceId: string;
  parentOkrId: string | null;
  title: string;
  description: string | null;
  ownerUserId: string | null;
  ownerOrgUnitId: string | null;
  timePeriod: OKRTimePeriod;
  periodLabel: string | null;
  startDate: string | null;
  endDate: string | null;
  status: OKRStatus;
  confidenceScore: number | null;
  strategicObjectiveId: string | null;
  tags: string[];
  sortOrder: number;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  // Joined
  keyResults?: OKRKeyResult[];
  childOkrs?: OKR[];
}

export interface OKRKeyResult {
  id: string;
  okrId: string;
  workspaceId: string;
  title: string;
  description: string | null;
  metricType: KeyResultMetricType;
  metricUnit: string | null;
  startValue: number | null;
  targetValue: number;
  currentValue: number;
  status: KeyResultStatus;
  confidence: KeyResultConfidence;
  sortOrder: number;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  // Joined
  checkIns?: OKRCheckIn[];
}

export interface OKRCheckIn {
  id: string;
  keyResultId: string;
  workspaceId: string;
  value: number;
  note: string | null;
  checkedInBy: string | null;
  checkedInAt: string;
}

// Progress helpers

export function keyResultProgress(kr: OKRKeyResult): number {
  const range = (kr.targetValue ?? 100) - (kr.startValue ?? 0);
  if (range === 0) return kr.currentValue >= kr.targetValue ? 100 : 0;
  const progress = ((kr.currentValue - (kr.startValue ?? 0)) / range) * 100;
  return Math.min(100, Math.max(0, Math.round(progress)));
}

export function okrProgress(okr: OKR): number {
  const krs = okr.keyResults ?? [];
  if (krs.length === 0) return 0;
  const total = krs.reduce((sum, kr) => sum + keyResultProgress(kr), 0);
  return Math.round(total / krs.length);
}

export const OKR_STATUS_LABELS: Record<OKRStatus, string> = {
  draft: 'Draft',
  active: 'Active',
  at_risk: 'At Risk',
  behind: 'Behind',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

export const OKR_STATUS_COLORS: Record<OKRStatus, { bg: string; text: string; border: string }> = {
  draft:     { bg: 'bg-slate-100',   text: 'text-slate-600',   border: 'border-slate-200' },
  active:    { bg: 'bg-emerald-50',  text: 'text-emerald-700', border: 'border-emerald-200' },
  at_risk:   { bg: 'bg-amber-50',    text: 'text-amber-700',   border: 'border-amber-200' },
  behind:    { bg: 'bg-red-50',      text: 'text-red-700',     border: 'border-red-200' },
  completed: { bg: 'bg-indigo-50',   text: 'text-indigo-700',  border: 'border-indigo-200' },
  cancelled: { bg: 'bg-gray-50',     text: 'text-gray-500',    border: 'border-gray-200' },
};

export const KR_STATUS_COLORS: Record<KeyResultStatus, { bg: string; text: string }> = {
  not_started: { bg: 'bg-slate-100',  text: 'text-slate-600' },
  on_track:    { bg: 'bg-emerald-100', text: 'text-emerald-700' },
  at_risk:     { bg: 'bg-amber-100',  text: 'text-amber-700' },
  behind:      { bg: 'bg-red-100',    text: 'text-red-700' },
  completed:   { bg: 'bg-indigo-100', text: 'text-indigo-700' },
  cancelled:   { bg: 'bg-gray-100',   text: 'text-gray-500' },
};

export const KR_CONFIDENCE_COLORS: Record<KeyResultConfidence, string> = {
  high:   'text-emerald-600',
  medium: 'text-amber-600',
  low:    'text-red-600',
};
